import React, { useState } from "react";
import {
  Plus,
  Download,
  MoreHorizontal,
  Activity,
  AlertCircle,
  Gauge,
  Loader2,
  ScanBarcode,
} from "lucide-react";
import { ShopfloorScannerModal } from "@/components/factory/ShopfloorScannerModal";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ListPageShell } from "@/components/shared/ListPageShell";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import { trpcClient } from "@/lib/trpc";
import type { PaginatedData } from "@/shared/schemas";
import { MachineLogForm } from "@/components/factory/MachineLogForm";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "logId", label: "Mã NK", width: 14 },
  { key: "machineName", label: "Máy", width: 20 },
  { key: "operatorName", label: "Vận hành", width: 20 },
  { key: "logType", label: "Loại NK", width: 16 },
  { key: "outputQty", label: "Sản lượng", width: 14, format: "number" },
  { key: "wasteQty", label: "Phế phẩm", width: 14, format: "number" },
  { key: "downtimeMinutes", label: "TG Dừng (phút)", width: 16, format: "number" },
  { key: "notes", label: "Ghi chú", width: 25 },
];

export default function MachineLogsPage({ viewSwitcher }: { viewSwitcher?: React.ReactNode } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  // Bản ghi đang sửa (null = ghi mới). Trước đây form luôn nhận initialData={undefined} nên không sửa được.
  const [editingLog, setEditingLog] = useState<any>(null);
  const { options: shiftOptions } = useConfigLookup("shifts");
  const shiftLabel = (code?: string | null) =>
    (code && shiftOptions.find((o: any) => o.id === code)?.label) || code || "-";
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter("all");

  // Fetch machine logs from API
  const { data, isLoading, error } = useQuery({
    queryKey: [
      "machine-logs",
      currentPage,
      pageSize,
      activeTab,
      dateParams,
    ],
    queryFn: async () => {
      const params: Record<string, any> = {
        page: currentPage,
        limit: pageSize,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
      };
      if (activeTab === "operation") params.type = "OPERATION";
      if (activeTab === "downtime") params.type = "DOWNTIME";
      return (await trpcClient.machineLogs.list.query(
        params,
      )) as PaginatedData<any>;
    },
  });

  const machineLogs = data?.items || [];
  const totalItems = data?.meta?.total || 0;

  // Filter logic (client-side filtering if needed, but API should handle it)
  const filteredData = machineLogs;

  const rowIds = filteredData.map((i: any) => i.logId);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    clearSelection,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    selectedCount,
  } = useRowSelection(rowIds);

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "nhat_ky_may",
    localData: filteredData,
    selectedIds,
    idKey: "logId",
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    return [
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
  };

  return (
    <ListPageShell
        trashModule="machine-logs"
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      tabs={viewSwitcher || undefined}
      dateFilter={
        <DatePeriodFilter
          period={period}
          setPeriod={setPeriod}
          customFrom={customFrom}
          setCustomFrom={setCustomFrom}
          customTo={customTo}
          setCustomTo={setCustomTo}
        />
      }
      headerFilters={
        <PageFilterTags
            items={[
              { key: "all", label: "Tất cả" },
              { key: "operation", label: "Vận hành" },
              { key: "downtime", label: "Dừng máy" },
            ]}
            selectedKeys={[activeTab]}
            onSelect={(key) => setActiveTab(key || "all")}
            dropdownLabel="Loại nhật ký"
          />
      }
      actions={
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs bg-white px-2.5"
            onClick={() => handleExport()}
            disabled={isExporting}
          >
            <Download className="mr-1.5 h-3 w-3" /> Xuất Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScannerOpen(true)}
            className="h-7 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200 px-2.5"
          >
            <ScanBarcode className="w-3.5 h-3.5 mr-1.5 text-indigo-600" />
            Quét Mã Vạch
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditingLog(null);
              setShowAddDialog(true);
            }}
            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 px-2.5"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Ghi sản lượng
          </Button>
        </div>
      }
    >
      <Card className="p-0 border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table className={cn(selectionMode && "apv-select-mode")}>
          <TableHeader className="apv-table-header">
            <TableRow>
              <TableHead
                className={cn("apv-col-checkbox w-10 text-center px-4")}
              >
                <DataTableSelectAllCheckbox
                  rowSelection={
                    { allSelected, toggleSelectAll, selectionMode } as any
                  }
                  asChild
                />
              </TableHead>
              <TableHead className="apv-col-code font-semibold ">
                Mã Log & Thời gian
              </TableHead>
              <TableHead className="font-semibold ">Máy & Lệnh SX</TableHead>
              <TableHead className="font-semibold ">
                Chi tiết Vận hành / Sự cố
              </TableHead>
              <TableHead className="text-center font-semibold ">
                Thời gian
              </TableHead>
              <TableHead className="text-center font-semibold ">
                Trạng thái
              </TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="h-[200px] text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    <span className="text-sm text-slate-500">
                      Đang tải dữ liệu...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="h-[200px] text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <AlertCircle className="h-6 w-6 text-rose-500" />
                    <span className="text-sm text-rose-600">
                      Có lỗi xảy ra khi tải dữ liệu
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () =>
                        await queryClient.invalidateQueries({
                          queryKey: ["machine-logs"],
                        })
                      }
                    >
                      Thử lại
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-[200px] text-center text-slate-500"
                >
                  Không có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <DataTableRow
                  key={item.logId}
                  id={item.logId}
                  rowSelection={
                    {
                      selectedIds,
                      handleRowMouseDown,
                      handleRowMouseUp,
                      handleRowMouseLeave,
                      wasLongPress,
                      selectionMode,
                      handleRowSelect,
                    } as any
                  }
                  onClick={() => {
                    if (selectionMode) return;
                    setSelectedLog(item);
                    setSheetOpen(true);
                  }}
                >
                  <TableCell
                    className={cn("px-4 apv-col-checkbox align-middle")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DataTableRowCheckbox
                      asChild
                      id={item.logId}
                      rowSelection={{ selectedIds, toggleSelect } as any}
                    />
                  </TableCell>
                  <TableCell className="apv-col-code align-middle">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-700 text-[13px] group-hover:text-indigo-600">
                        {item.logId}
                      </span>
                      <span className="text-[11px] text-slate-500 font-mono">
                        {new Date(item.date).toLocaleDateString("vi-VN")} •{" "}
                        {shiftLabel(item.shift)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="align-middle">
                    <div className="flex items-center gap-2.5">
                      <div onClick={(e) => e.stopPropagation()}>
                        <ProductionThumbnailCell
                          imageUrl={item.productionOrder?.product?.image}
                          layoutUrl={item.productionOrder?.printLayouts?.[0]?.fileUrl}
                          title={item.productionOrder?.productName || item.productionOrderId || item.logId}
                          subtitle={item.productionOrder?.orderId || item.logId}
                          type="auto"
                          size={32}
                          className="shrink-0"
                        />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-semibold text-slate-900 text-[13px]">
                          {item.machine?.name ||
                            item.machine?.machineId ||
                            item.machineId}
                        </span>
                        <span className="text-[11px] text-slate-500 font-mono">
                          {item.productionOrder?.orderId || "-"}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="align-middle">
                    <div className="flex flex-col max-w-[300px]">
                      {item.type === "OPERATION" ? (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                          {Object.entries(item.parameters || {}).map(
                            ([key, val]) => (
                              <span
                                key={key}
                                className="text-[11px] text-slate-600 flex items-center gap-1"
                              >
                                <Gauge className="w-3 h-3 text-slate-400" />
                                <span className="capitalize">{key}:</span>{" "}
                                <b>{val as any}</b>
                              </span>
                            ),
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <span className="text-[12px] font-medium text-rose-700">
                            {item.reason || "-"}
                          </span>
                          <span className="text-[11px] text-slate-500 italic">
                            Fix: {item.fixAction || "-"}
                          </span>
                        </div>
                      )}
                      {item.operator && (
                        <span className="text-[10px] text-slate-400 mt-0.5">
                          Op:{" "}
                          {typeof item.operator === "object" &&
                          item.operator !== null
                            ? (item.operator as any).fullName
                            : item.operator}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center align-middle">
                    <span className="font-mono text-[13px] font-medium text-slate-700">
                      {item.durationMinutes ? `${item.durationMinutes}m` : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-center align-middle">
                    <div className="flex justify-center">
                      <StatusBadge
                        type="MachineLogType"
                        value={item.status || "Unknown"}
                      />
                    </div>
                  </TableCell>
                  <TableCell
                    className="px-4 text-right align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-7 w-7 p-0 hover:bg-slate-200"
                        >
                          <MoreHorizontal className="h-4 w-4 text-slate-400" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuLabel>Thao tác</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </DataTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Machine Log Detail Dialog */}
      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedLog && (
            <>
              <DialogHeader className="pb-4 border-b">
                <DialogTitle className="text-lg">
                  {selectedLog.logId}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge
                    className={
                      selectedLog.type === "OPERATION"
                        ? "bg-emerald-100 text-emerald-800 border-none"
                        : selectedLog.type === "DOWNTIME"
                          ? "bg-rose-100 text-rose-800 border-none"
                          : "bg-blue-100 text-blue-800 border-none"
                    }
                  >
                    {selectedLog.type === "OPERATION"
                      ? "Vận hành"
                      : selectedLog.type === "DOWNTIME"
                        ? "Dừng máy"
                        : selectedLog.type}
                  </Badge>
                </div>
              </DialogHeader>
              <div className="space-y-5 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Ngày</p>
                    <p className="text-sm font-medium">
                      {new Date(selectedLog.date).toLocaleDateString("vi-VN")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Ca</p>
                    <p className="text-sm font-medium">
                      {shiftLabel(selectedLog.shift)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Máy</p>
                    <p className="text-sm font-bold font-mono text-indigo-600">
                      {selectedLog.machine?.name ||
                        selectedLog.machine?.machineId ||
                        selectedLog.machineId}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Lệnh SX</p>
                    <p className="text-sm font-medium font-mono">
                      {selectedLog.productionOrder?.orderId || "-"}
                    </p>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground">Thời gian</p>
                  <p className="text-sm font-bold">
                    {selectedLog.durationMinutes} phút
                  </p>
                </div>
                {selectedLog.operator && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Người vận hành
                      </p>
                      <p className="text-sm font-medium">
                        {typeof selectedLog.operator === "object" &&
                        selectedLog.operator !== null
                          ? (selectedLog.operator as any).fullName
                          : selectedLog.operator}
                      </p>
                    </div>
                  </>
                )}
                {selectedLog.type === "DOWNTIME" && selectedLog.reason && (
                  <>
                    <Separator />
                    <div className="bg-rose-50 border border-rose-100 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-bold text-rose-700">
                        Sự cố dừng máy
                      </p>
                      <p className="text-sm text-rose-900">
                        {selectedLog.reason}
                      </p>
                      {selectedLog.fixAction && (
                        <p className="text-xs text-rose-600">
                          Khắc phục: {selectedLog.fixAction}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSheetOpen(false);
                    navigate({
                      to: "/machine-logs/$logId",
                      params: { logId: selectedLog.logId },
                    });
                  }}
                >
                  <Activity className="w-4 h-4 mr-1.5" /> Xem đầy đủ
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="machine-log-edit"
                  onClick={() => {
                    setSheetOpen(false);
                    setEditingLog(selectedLog);
                    setShowAddDialog(true);
                  }}
                >
                  Sửa
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog using MachineLogForm */}
      <MachineLogForm
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSubmit={async (payload, id) => {
          try {
            if (id) {
              await trpcClient.machineLogs.update.mutate({ id, data: payload as any });
              toast.success("Đã cập nhật nhật ký máy");
            } else {
              const created: any = await trpcClient.machineLogs.create.mutate(payload as any);
              toast.success(`Đã ghi nhật ký máy ${created?.logId || ""}`.trim());
            }
            setShowAddDialog(false);
            setEditingLog(null);
            await queryClient.invalidateQueries({ queryKey: ["machine-logs"] });
          } catch (error: any) {
            toast.error(error?.message || "Không thể lưu nhật ký");
          }
        }}
        initialData={editingLog ?? undefined}
      />

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
      <ShopfloorScannerModal open={scannerOpen} onOpenChange={setScannerOpen} />
    </ListPageShell>
  );
}
